import {
	ActionIcon,
	Box,
	createStyles,
	getStylesRef,
	Group,
	Skeleton,
	Stack,
	Tooltip,
} from '@mantine/core';
import { useClipboard, useDidUpdate, useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
	apiGetIntegrationUserCredentialsQueryKey,
	useApiGetIntegrationUserCredentials,
	useApiTestUserCredentialsConnection,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { Button, Icon, IconButton, Text } from '@repo/foundations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounceFn } from 'ahooks';
import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import type { IMetric } from '../../../../../../api';
import {
	useAuthUser,
	useIntegration,
	useUpdateMetric,
	useWorkspace,
} from '../../../../../../api';
import { useIntegrationOAuthConnect } from '../../../../../../features/IntegrationOauthConnect';

import { useDuplicateMetric } from '../../../../../../api/hooks/metric/useDuplicateMetric';
import { useMetricExecution } from '../../../../../../api/hooks/metric/useMetricExecution';
import { useGetOrCreateMetric } from '../../../../../../api/hooks/metricTerm/useGetOrCreateMetric';
import { invalidateResourceRelationList } from '../../../../../../api/hooks/relations/useResourceRelationList';
import { entityIdFromCurrentPath } from '../../../../../../utils/shared.utils';
import { captureError } from '../../../../../../web-tracing';
import { IntegrationSelector } from '../../../../../EntityModal/Metadata/IntegrationSelector';
import { ErrorBoundary } from '../../../../../ErrorBoundary';
import IntegrationLogo from '../../../../../IntegrationLogo';
import { openModal } from '../../../../../ModalManager';
import { useModals } from '../../../../../ModalManager/hooks';
import { UserLevelCredentialsModal } from '../../../../../Settings/UserLevelCredentialsSettings/UserLevelCredentialsModal';
import type { EditorRefHandle } from '../../../../../Sql/SqlEditor/SqlEditor';
import SqlEditor from '../../../../../Sql/SqlEditor/SqlEditor';
import type { CreateGraphModalResult } from '../ChartBlock/CreateGraphModal';
import { CreateGraphModal } from '../ChartBlock/CreateGraphModal';
import { ExportCsvFileButton } from './ExportCsvFileButton';
import { MoreActionsMenu } from './MoreActionsMenu';
import { QueryBlockErrorFallback } from './QueryBlockErrorFallback';
import { QueryBlockPlaceholder } from './QueryBlockPlaceholder';
import { QueryBlockRenameModal } from './QueryBlockRenameModal';
import { QueryBlockTitle } from './QueryBlockTitle';
import { ResultsError } from './ResultsError';
import { ResultsTable } from './ResultsTable';

const useStyles = createStyles(
	(
		theme,
		{ isSelected, compactMode }: { isSelected: boolean; compactMode?: boolean }
	) => ({
		wrapper: {
			background: compactMode
				? theme.other.getColor('surface/primary/default')
				: theme.other.getColor('surface/secondary/default'),
			borderRadius: theme.radius.md,
			border: `solid ${theme.other.borderWidth.sm}px ${isSelected ? theme.other.getColor('border/secondary/active') : theme.other.getColor('border/secondary/default')}`,

			[`&:hover .${getStylesRef('hideIfNotHovered')}, &:focus-within .${getStylesRef('hideIfNotHovered')}`]:
				{
					opacity: 1,
				},
		},
		titleInput: {
			background: 'transparent',
			color: theme.other.getColor('text/primary/default'),
			border: 0,
			fontWeight: theme.other.typography.weight.bold,
			fontSize: theme.fontSizes.sm,
			lineHeight: theme.other.typography.lineHeight.text.sm,
			height: 'unset',
			paddingLeft: theme.spacing['3xs'],
		},
		sqlEditor: {
			padding: `0 ${theme.spacing.md} ${theme.spacing.md}`,
		},
		floatingToggleQuery: {
			position: 'absolute',
			top: 0,
			left: -48,
		},
		floatingToggleResults: {
			position: 'absolute',
			top: 0,
			left: -44,
		},
		hideIfNotHovered: {
			ref: getStylesRef('hideIfNotHovered'),
			opacity: 0,
		},
		expandCollapseButton: {
			opacity: 0,
		},
	})
);

export interface QueryBlockContainerProps {
	defaultQueryOpen?: boolean;
	defaultResultsOpen?: boolean;
	onDuplicate?: (newMetricId: string) => void;
	onCopyLink?: () => void;
	onAddGraph?: (result: CreateGraphModalResult) => void;
	id: string;
	// integrationId is only used at the creation of a new query block
	integrationId?: string;
	readOnly: boolean;
	onDelete: VoidFunction;
	isSelected?: boolean;
	onChangeId?: (id: string) => void;
	noParentMode?: boolean;
	compactMode?: boolean;
	onSqlChange?: (sql: string) => void;
	onIntegrationChange?: (integrationId: string) => void;
	actionButtons?: JSX.Element;
	onExecutionEnd?: (metricId: string) => void;
}

function QueryBlockContainerInternal({
	defaultQueryOpen = true,
	defaultResultsOpen = true,
	id,
	integrationId,
	readOnly,
	onDelete,
	isSelected,
	onDuplicate,
	onCopyLink,
	onAddGraph,
	onChangeId,
	noParentMode = false,
	compactMode = false,
	onSqlChange,
	onIntegrationChange,
	onExecutionEnd,
}: QueryBlockContainerProps) {
	const { classes } = useStyles({ isSelected: !!isSelected, compactMode });
	const { copy } = useClipboard();
	const { workspace } = useWorkspace();
	const { user } = useAuthUser();
	const editorRef = useRef<EditorRefHandle>();
	const queryClient = useQueryClient();
	const { closeAll } = useModals();

	const [credentialsUpdateTrigger, setCredentialsUpdateTrigger] = useState(0);

	const { data: metric } = useGetOrCreateMetric({
		id,
		integration: !!integrationId ? integrationId : undefined,
		hidden: true,
		parent: noParentMode ? undefined : entityIdFromCurrentPath(),
	});

	useDidUpdate(() => {
		if (metric?.id && metric.id !== id) {
			onChangeId?.(metric.id);
		}
	}, [metric?.id, onChangeId, id]);

	const { mutateAsync } = useUpdateMetric({});

	const updateMetric = useCallback(
		async (entries: Partial<IMetric>) => {
			if (!metric) {
				return;
			}

			trackEvent(
				'metric/embedded/update',
				{
					id: metric.id,
				},
				user,
				workspace
			);
			const updated = Object.entries(entries).reduce(
				(acc, [key, value]) => ({
					...acc,
					[key]: value,
				}),
				{}
			);
			await mutateAsync({ data: { id, ...updated }, optimisticUpdate: false });
		},
		[id, metric, mutateAsync, user, workspace]
	);

	const [showQuery, { toggle: toggleQuery }] = useDisclosure(defaultQueryOpen);
	const [showResults, { toggle: toggleResults, open: openResults }] =
		useDisclosure(defaultResultsOpen);
	const [renameModalOpened, { toggle: toggleRenameModal }] =
		useDisclosure(false);
	const [
		createGraphModalOpened,
		{ toggle: toggleCreateGraphModal, close: closeCreateGraphModal },
	] = useDisclosure(false);

	const { data: integration } = useIntegration({
		id: metric.integration,
		options: {
			enabled: !!metric.integration,
		},
	});

	// Get integration spec to check if user-level credentials are required
	const spec = integrationList.find((s: any) => s.type === integration?.type);
	const requiresUserCredentials = !!(
		integration?.query_execute_use_user_level_credentials && spec
	);

	// Get user credentials if required (for detecting when they're updated)
	const { data: userCredentials } = useApiGetIntegrationUserCredentials(
		{
			pathParams: { integrationId: integration?.id ?? '' },
		},
		{
			enabled: requiresUserCredentials && !!integration?.id,
		}
	);

	// Test user credentials connection
	const { mutateAsync: testCredentials } = useApiTestUserCredentialsConnection(
		{}
	);

	// Check credentials validity via useQuery
	const credentialsQueryKey = [
		'user-credentials-status',
		integration?.id,
		userCredentials,
		credentialsUpdateTrigger,
	] as const;

	const { data: credentialsQueryData, isLoading: isCheckingCredentials } =
		useQuery({
			queryKey: credentialsQueryKey,
			queryFn: async () => {
				if (!integration?.id) {
					return {
						hasCredentials: false,
						isValid: false,
						error: undefined,
					};
				}

				try {
					const result = await testCredentials({
						pathParams: { integrationId: integration.id },
					});

					if (!result.success) {
						return {
							hasCredentials: true,
							isValid: false,
							error: result.error ?? 'Unknown error',
						};
					}
					return {
						hasCredentials: true,
						isValid: true,
						error: undefined,
					};
				} catch {
					return {
						hasCredentials: false,
						isValid: false,
						error: undefined,
					};
				}
			},
			enabled: requiresUserCredentials && !!integration?.id,
		});

	// Derive credentials status from query state
	const credentialsStatus = useMemo(() => {
		if (!requiresUserCredentials) {
			return {
				hasCredentials: true,
				isValid: true,
				isChecking: false,
				error: undefined,
			};
		}

		return {
			hasCredentials: credentialsQueryData?.hasCredentials ?? false,
			isValid: credentialsQueryData?.isValid ?? false,
			isChecking: isCheckingCredentials,
			error: credentialsQueryData?.error,
		};
	}, [requiresUserCredentials, credentialsQueryData, isCheckingCredentials]);

	const handleCredentialsConfigured = useCallback(() => {
		// Invalidate the user credentials query to refetch
		queryClient.invalidateQueries({
			queryKey: apiGetIntegrationUserCredentialsQueryKey({
				pathParams: { integrationId: integration?.id ?? '' },
			}),
		});

		// Invalidate our credentials status query to re-check
		queryClient.invalidateQueries({
			queryKey: ['user-credentials-status', integration?.id],
		});

		// Trigger a credentials re-check (for query key update)
		setCredentialsUpdateTrigger((prev) => prev + 1);
	}, [queryClient, integration?.id]);

	const handleConfigureCredentials = useCallback(() => {
		if (!integration || !spec) return;

		openModal({
			title: `Configure ${spec.name} credentials`,
			children: (
				<UserLevelCredentialsModal
					integration={integration}
					onCredentialsSaved={handleCredentialsConfigured}
				/>
			),
			size: 'lg',
		});
	}, [integration, spec, handleCredentialsConfigured]);

	const { run: handleChangeQuery, flush: flushChangeQueryDebounce } =
		useDebounceFn(
			(value: string) => {
				onSqlChange?.(value);
				updateMetric({ sql: value });
			},
			{
				wait: 500,
			}
		);

	const { mutateAsync: duplicateMetric } = useDuplicateMetric();

	const { execute, executionStatus, isExecuting } = useMetricExecution(
		metric,
		undefined,
		() => {
			openResults();
			onExecutionEnd?.(metric.id);

			const parentEntityId = !noParentMode ? entityIdFromCurrentPath() : null;
			if (parentEntityId) {
				invalidateResourceRelationList(parentEntityId);
			}
		}
	);

	const handleChangeIntegration = useCallback(
		(newIntegrationId: string) => {
			onIntegrationChange?.(newIntegrationId);
			updateMetric({ integration: newIntegrationId });
		},
		[updateMetric]
	);

	const handleExecute = useCallback(async () => {
		// Check if user-level credentials are required but not valid
		if (requiresUserCredentials && !credentialsStatus.isValid) {
			// Open credentials modal for both missing and invalid credentials
			handleConfigureCredentials();
			return;
		}

		// make sure pending changes are flushed to API before executing
		await flushChangeQueryDebounce();
		await execute();
	}, [
		requiresUserCredentials,
		credentialsStatus.isValid,
		handleConfigureCredentials,
		execute,
		flushChangeQueryDebounce,
	]);

	const handleCopyCode = useCallback(() => {
		copy(metric.sql);
		showNotification({
			message: `Code copied`,
		});
	}, [copy, metric.sql]);

	const handleDuplicate = useCallback(async () => {
		try {
			const newMetric = await duplicateMetric({ entity: metric });
			onDuplicate?.(newMetric.id);
		} catch (error) {
			captureError(error);
		}
	}, [duplicateMetric, metric, onDuplicate]);

	const handleCreateGraph = useCallback(
		async (result: CreateGraphModalResult) => {
			closeCreateGraphModal();
			onAddGraph?.(result);
			const {
				xAxis: time,
				yAxis: primary,
				dimension,
				numericFormat: numeric_format,
			} = result;
			updateMetric({
				time,
				primary,
				dimension,
				numeric_format,
			});
		},
		[closeCreateGraphModal, onAddGraph, updateMetric]
	);

	const handleChangeSchedule = useCallback(
		(value: number | null) => {
			updateMetric({ scheduled_delta: value });
		},
		[updateMetric]
	);

	const handleFormatQuery = useCallback(() => {
		editorRef.current?.formatQuery();
	}, [editorRef]);

	const canShowResults = showResults && !isExecuting;
	const hasExecutionFailed = executionStatus?.status === 'failed';
	const hasResults = metric.results.length > 0;

	// Check if the error is an OAuth authentication error
	const isOAuthError = useMemo(() => {
		const errorMessage = executionStatus?.logs ?? '';
		return (
			hasExecutionFailed && errorMessage.includes('has not connected via OAuth')
		);
	}, [hasExecutionFailed, executionStatus?.logs]);

	// OAuth connect mutation
	const oauthConnectMutation = useIntegrationOAuthConnect(
		metric.integration,
		integration?.type,
		{ openInNewTab: true }
	);

	const renderRunButton = () => {
		// Debug logging (only in development)
		if (process.env.NODE_ENV === 'development') {
			// eslint-disable-next-line no-console
			console.log('QueryBlock renderRunButton:', {
				requiresUserCredentials,
				credentialsStatus,
				isExecuting,
			});
		}

		if (isExecuting) {
			return (
				<Button size="md" disabled>
					<Icon
						name="loader"
						style={{ animation: 'spin 2s linear infinite' }}
					/>
				</Button>
			);
		}

		if (credentialsStatus.isChecking) {
			return (
				<Button size="md" disabled>
					<Icon
						name="loader"
						style={{ animation: 'spin 2s linear infinite' }}
					/>
					Checking credentials...
				</Button>
			);
		}

		if (requiresUserCredentials && !credentialsStatus.isValid) {
			return (
				<Button
					leftIconName="playerPlay"
					size="md"
					onClick={handleExecute}
					disabled={readOnly ? true : undefined}
					tone={credentialsStatus.hasCredentials ? 'critical' : 'default'}
				>
					{credentialsStatus.hasCredentials
						? 'Fix credentials'
						: 'Configure credentials'}
				</Button>
			);
		}

		return (
			<Button
				leftIconName="playerPlay"
				size="md"
				onClick={handleExecute}
				disabled={readOnly ? true : undefined}
			>
				Run
			</Button>
		);
	};

	return (
		<Stack className={classes.wrapper} spacing={0} id={`metric-${metric.id}`}>
			<Group spacing="sm" p="sm" noWrap>
				<Box style={{ flex: 1, position: 'relative' }}>
					{compactMode ? (
						<Text size="sm">SQL</Text>
					) : (
						<>
							<QueryBlockTitle metric={metric} readOnly={readOnly} />
							<Box className={classes.floatingToggleQuery}>
								<Tooltip
									label={showQuery ? 'Collapse section' : 'Expand section'}
								>
									<IconButton
										variant="tertiary"
										iconName={showQuery ? 'chevronDown' : 'chevronRight'}
										onClick={toggleQuery}
									/>
								</Tooltip>
							</Box>
						</>
					)}
				</Box>
				<Group spacing="xs" noWrap className={classes.hideIfNotHovered}>
					<Group spacing="3xs" noWrap>
						{readOnly ? (
							<Box p="4xs">
								<IntegrationLogo
									integrationType={integration?.type}
									size={16}
								/>
							</Box>
						) : (
							<IntegrationSelector
								withinPortal={false}
								initialValue={metric.integration}
								onChange={handleChangeIntegration}
								target={
									<Tooltip label="Select integration">
										<ActionIcon disabled={isExecuting}>
											<IntegrationLogo
												integrationType={integration?.type}
												size={16}
											/>
										</ActionIcon>
									</Tooltip>
								}
								readOnly={isExecuting}
								withOnlyQueryableIntegrations
							/>
						)}
						{!compactMode && (
							<MoreActionsMenu
								readOnly={readOnly}
								metric={metric}
								handleDuplicate={handleDuplicate}
								toggleRenameModal={toggleRenameModal}
								onChangeSchedule={handleChangeSchedule}
								onCopyLink={onCopyLink}
								handleCopyCode={handleCopyCode}
								onDelete={onDelete}
								onFormatQuery={handleFormatQuery}
							/>
						)}
						{renameModalOpened && (
							<QueryBlockRenameModal
								metric={metric}
								onChange={updateMetric}
								onClose={toggleRenameModal}
							/>
						)}
					</Group>
					{renderRunButton()}
				</Group>
			</Group>
			{showQuery && (
				<SqlEditor
					lineNumbers
					className={classes.sqlEditor}
					defaultValue={metric.sql}
					onChange={handleChangeQuery}
					readOnly={readOnly || isExecuting}
					minHeight={274}
					collapseHeight={360}
					classNames={{
						expandCollapseButton: classes.expandCollapseButton,
					}}
					onExecute={handleExecute}
					collapseBackgroundColor="surface/secondary/default"
					editorRef={editorRef}
				/>
			)}
			{(hasResults || hasExecutionFailed) && (
				<Group px="xs" pb="xs" spacing="3xs">
					<Box style={{ flex: 1, position: 'relative' }}>
						<Button
							size="sm"
							variant="tertiary"
							onClick={toggleResults}
							disabled={isExecuting}
						>
							{canShowResults ? 'Hide results' : 'Show results'}
						</Button>
						{!compactMode && (
							<Box className={classes.floatingToggleResults}>
								<IconButton
									tooltip={
										canShowResults ? 'Collapse section' : 'Expand section'
									}
									variant="tertiary"
									iconName={canShowResults ? 'chevronDown' : 'chevronRight'}
									onClick={toggleResults}
									disabled={isExecuting}
								/>
							</Box>
						)}
					</Box>
					{!readOnly && hasResults && onAddGraph && (
						<Button
							size="sm"
							variant="tertiary"
							leftIconName="graph"
							disabled={isExecuting}
							onClick={toggleCreateGraphModal}
						>
							Graph results
						</Button>
					)}
					{hasResults && !compactMode && (
						<ExportCsvFileButton metric={metric} disabled={isExecuting} />
					)}
				</Group>
			)}
			{canShowResults && !hasExecutionFailed && (
				<ResultsTable
					results={metric.results}
					columnTypes={metric.results_column_types}
					lastUpdatedAt={metric.last_run}
				/>
			)}
			{canShowResults && hasExecutionFailed && (
				<ResultsError
					title={isOAuthError ? 'Authentication required' : 'Query error'}
					errorMessage={
						isOAuthError
							? 'Connect your account to run queries in Secoda'
							: (executionStatus?.logs ?? 'Unknown error')
					}
				>
					{isOAuthError && (
						<Button
							variant="default"
							size="sm"
							rightIconName="arrowUpRight"
							onClick={() => oauthConnectMutation.mutate()}
							loading={oauthConnectMutation.isPending}
						>
							Connect
						</Button>
					)}
				</ResultsError>
			)}
			{createGraphModalOpened && (
				<CreateGraphModal
					initialMetricId={metric.id}
					onClose={closeCreateGraphModal}
					onSave={handleCreateGraph}
					initialXAxis={metric.time}
					initialYAxis={metric.primary}
					initialDimension={metric.dimension}
					initialNumericFormat={metric.numeric_format}
				/>
			)}
		</Stack>
	);
}

function errorBoundaryFallbackFn(error: {
	error: unknown;
	componentStack: string;
	eventId: string;
	resetError(): void;
}) {
	return <QueryBlockErrorFallback error={error.error as Error} />;
}

export function QueryBlockContainer({
	shouldRenderPlaceholder,
	...props
}: QueryBlockContainerProps & { shouldRenderPlaceholder?: boolean }) {
	return (
		<ErrorBoundary fallback={errorBoundaryFallbackFn}>
			<Suspense fallback={<Skeleton height={300} />}>
				{shouldRenderPlaceholder ? (
					<QueryBlockPlaceholder {...props} />
				) : (
					<QueryBlockContainerInternal {...props} />
				)}
			</Suspense>
		</ErrorBoundary>
	);
}
